import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload, Trash2, RefreshCw, User, ImageIcon, MapPin } from "lucide-react";
import { api, fileUrl, errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import SearchableSelect from "../components/SearchableSelect";
import PhotoCropDialog from "../components/PhotoCropDialog";
import DateInput from "../components/DateInput";
import WilayahCascade from "../components/WilayahCascade";
import { KameraFotoDialog } from "../components/KameraFotoDialog";
import { Camera } from "lucide-react";

// Alamat KTP (utama, wajib) & Alamat Domisili (opsional) — tersimpan sebagai objek; backend menyinkronkan KTP ke field alamat datar
const KOSONG_ALAMAT = { detail: "", provinsi: "", kota: "", kecamatan: "", kelurahan: "", kode_pos: "" };
const AlamatTambahan = ({ judul, value, onChange, testId, required, error }) => {
  const v = { ...KOSONG_ALAMAT, ...(value || {}) };
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setMany = (obj) => onChange({ ...v, ...obj });
  const kosong = () => onChange(null);
  return (
    <div className={`rounded-2xl border p-3 sm:p-4 space-y-3 ${error ? "border-red-400" : "border-slate-200"}`} data-testid={testId}>
      <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase tracking-wide text-[hsl(224,64%,33%)]">{judul} {required ? <span className="text-red-500">*</span> : <span className="font-normal normal-case text-slate-400">(opsional)</span>}</div>{!required && Object.values(v).some(Boolean) && <button type="button" className="text-xs text-slate-500 hover:underline" onClick={kosong} data-testid={`${testId}-kosongkan`}>Kosongkan</button>}</div>
      {error && <p className="text-xs text-red-600" data-testid={`${testId}-error`}>{error}</p>}
      <Field label="Detail Alamat (nomor rumah, RT/RW, dll)" required={required}><Textarea value={v.detail} onChange={(e) => set("detail", e.target.value)} rows={2} className="rounded-xl" data-testid={`${testId}-detail`} /></Field>
      <WilayahCascade form={v} set={set} setMany={setMany} testPrefix={`${testId}-`} />
    </div>
  );
};
import { MultiSelect, TagInput } from "../components/MultiSelect";
import { gelar } from "../lib/utils";
import ParentPicker from "../components/ParentPicker";
import PasanganPicker from "../components/PasanganPicker";
import { DokumenJemaat } from "../components/DokumenJemaat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  nij: "", nama_lengkap: "", nama_panggilan: "", jenis_kelamin: "", status_jemaat: "",
  status_keanggotaan: "", asal_gereja: "", tanggal_pindah: "", nik: "", nomor_kk: "", tempat_lahir: "", tanggal_lahir: "",
  status_pernikahan: "", tanggal_pernikahan: "", tempat_pernikahan: "", nama_pasangan: "", pasangan_id: "", alamat: "", rt: "", rw: "", provinsi: "", kelurahan: "", kecamatan: "", kota: "", alamat_ktp: null, alamat_domisili: null,
  kode_pos: "", nomor_hp: "", whatsapp: "", email: "", link_google_maps: "", pendidikan: "", pekerjaan: "",
  instansi: "", jabatan: "", komisi: "", kelas_sm: "", care_leader: "", jabatan_gerejawi: "", keterangan: "",
  bidang_pelayanan: [], jenis_musik: [], pelayanan_lainnya: "",
  nama_ayah: "", nama_ibu: "", ayah_id: "", ibu_id: "",
  status_baptisan: "", tanggal_baptisan: "", tempat_baptisan: "",
};

export default function JemaatFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [master, setMaster] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [fotoPath, setFotoPath] = useState(null);
  const [kamera, setKamera] = useState(false);
  const [linkedKeluarga, setLinkedKeluarga] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/master/options").then((r) => setMaster(r.data)).catch(() => {});
    if (isEdit) {
      api.get(`/jemaat/${id}`).then((r) => {
        const d = r.data;
        const next = { ...EMPTY };
        Object.keys(EMPTY).forEach((k) => { next[k] = d[k] ?? (Array.isArray(EMPTY[k]) ? [] : ""); });
        setForm(next);
        setFotoPath(d.foto_path || null);
        setSavedPernikahan({ tanggal: d.tanggal_pernikahan || "", tempat: d.tempat_pernikahan || "" });
        setParents({ ayah: d.ayah || null, ibu: d.ibu || null });
        setPasangan(d.pasangan || null);
        setLinkedKeluarga(Boolean(d.keluarga_id));
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [id, isEdit]);

  const [savedPernikahan, setSavedPernikahan] = useState({ tanggal: "", tempat: "" });
  const [parents, setParents] = useState({ ayah: null, ibu: null });
  const [pasangan, setPasangan] = useState(null);
  const [pendingStatusPernikahan, setPendingStatusPernikahan] = useState(null);

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "status_baptisan" && v !== "Sudah") { next.tanggal_baptisan = ""; }
      if (k === "status_pernikahan" && v !== "Menikah") { next.tanggal_pernikahan = ""; next.tempat_pernikahan = ""; next.nama_pasangan = ""; next.pasangan_id = ""; }
      return next;
    });
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const changeBidangPelayanan = (arr) => {
    setForm((f) => ({ ...f, bidang_pelayanan: arr, jenis_musik: ["Keyboard","Guitar","Bass","Drum"].some((i) => arr.includes(i)) ? f.jenis_musik : [], pelayanan_lainnya: arr.includes("Lainnya") ? f.pelayanan_lainnya : "" }));
  };

  const changeStatusPernikahan = (v) => {
    const hasSaved = Boolean(savedPernikahan.tanggal || savedPernikahan.tempat);
    const hasData = Boolean(form.tanggal_pernikahan || form.tempat_pernikahan);
    if (form.status_pernikahan === "Menikah" && v !== "Menikah" && (hasSaved || hasData)) { setPendingStatusPernikahan(v); return; }
    set("status_pernikahan", v);
  };

  const validate = () => {
    const e = {};
    if (!form.nama_lengkap.trim()) e.nama_lengkap = "Nama lengkap wajib diisi";
    if (!form.jenis_kelamin) e.jenis_kelamin = "Jenis kelamin wajib dipilih";
    if (!form.status_jemaat) e.status_jemaat = "Status jemaat wajib dipilih";
    if (!form.status_keanggotaan) e.status_keanggotaan = "Status keanggotaan wajib dipilih";
    if (form.status_baptisan === "Sudah" && !form.tanggal_baptisan) e.tanggal_baptisan = "Tanggal baptisan wajib diisi jika sudah baptis";
    if (form.status_baptisan !== "Sudah" && form.tanggal_baptisan) e.tanggal_baptisan = "Tanggal baptisan harus kosong jika belum baptis";
    if (!(form.alamat_ktp?.detail || "").trim()) e.alamat_ktp = "Detail Alamat sesuai KTP wajib diisi";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Periksa kembali field wajib yang belum lengkap");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => { payload[k] = Array.isArray(v) ? v : (v === "" ? null : v); });
      payload.nij = (form.nij || "").trim() || null;
      payload.nama_lengkap = form.nama_lengkap.trim();
      let savedId = id;
      if (isEdit) {
        await api.put(`/jemaat/${id}`, payload);
      } else {
        const r = await api.post("/jemaat", payload);
        savedId = r.data.id;
      }
      toast.success(isEdit ? "Data jemaat diperbarui" : "Jemaat baru tersimpan");
      navigate(`/jemaat/${savedId}`);
    } catch (e) {
      const msg = errMsg(e);
      if (e?.response?.status === 409) setErrors((prev) => ({ ...prev, nij: msg }));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const MAX_FOTO_MB = 5;
  const [cropFile, setCropFile] = useState(null);

  const pickFile = (file) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!isEdit) { toast.error("Simpan data jemaat terlebih dahulu sebelum menambah foto"); return; }
    if (!file.type.startsWith("image/")) { toast.error("File harus berupa gambar (JPG, PNG, WEBP)"); return; }
    if (file.size > MAX_FOTO_MB * 1024 * 1024) { toast.error(`Ukuran foto maksimal ${MAX_FOTO_MB}MB`); return; }
    setCropFile(file);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/jemaat/${id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFotoPath(r.data.foto_path);
      setCropFile(null);
      toast.success("Foto berhasil disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFoto = async () => {
    setUploading(true);
    try {
      await api.delete(`/jemaat/${id}/foto`);
      setFotoPath(null);
      toast.success("Foto dihapus");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {isEdit ? "Edit Jemaat" : "Tambah Jemaat"}
          </h1>
          <p className="text-sm text-slate-500">Field bertanda <span className="text-red-500">*</span> wajib diisi</p>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["s1", "s2"]} className="space-y-3">
        <Section value="s1" title="1. Identitas Utama & Keanggotaan">
          <Field label="NIJ (Nomor Induk Jemaat)" error={errors.nij}>
            <Input data-testid="input-nij" value={isEdit ? form.nij : "Dibuat otomatis saat disimpan"} readOnly className="min-h-[44px] rounded-xl font-mono bg-slate-50 text-slate-600" />
            <p className="text-xs text-slate-500 mt-1" data-testid="nij-hint">Format 10 digit: cabang (11) · status keanggotaan · tahun · nomor urut. Contoh 1101260001. Bila status keanggotaan berubah, NIJ baru dibuat dan NIJ lama diarsipkan.</p>
          </Field>
          <Field label="Nama Lengkap" required error={errors.nama_lengkap}>
            <Input data-testid="input-nama-lengkap" value={form.nama_lengkap} onChange={(e) => set("nama_lengkap", e.target.value)} className="min-h-[44px] rounded-xl" />
          </Field>
          <Field label="Nama Panggilan">
            <Input data-testid="input-nama-panggilan" value={form.nama_panggilan} onChange={(e) => set("nama_panggilan", e.target.value)} className="min-h-[44px] rounded-xl" />
          </Field>
          <Field label="Jenis Kelamin" required error={errors.jenis_kelamin}>
            <SearchableSelect options={master.jenis_kelamin} value={form.jenis_kelamin} onChange={(v) => set("jenis_kelamin", v)} placeholder="Pilih jenis kelamin" testId="select-jenis-kelamin" allowClear={false} />
          </Field>
          <Field label="Status Jemaat" required error={errors.status_jemaat}>
            <SearchableSelect options={master.status_jemaat} value={form.status_jemaat} onChange={(v) => set("status_jemaat", v)} placeholder="Pilih status jemaat" testId="select-status-jemaat" allowClear={false} />
          </Field>
          <Field label="Status Keanggotaan" required error={errors.status_keanggotaan}>
            <SearchableSelect options={master.status_keanggotaan} value={form.status_keanggotaan} onChange={(v) => set("status_keanggotaan", v)} placeholder="Pilih status keanggotaan" testId="select-status-keanggotaan" allowClear={false} />
          </Field>
          {form.status_keanggotaan === "Jemaat Pindahan" && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Asal Gereja">
              <Input data-testid="input-asal-gereja" value={form.asal_gereja || ""} onChange={(e) => set("asal_gereja", e.target.value)} placeholder="mis. GKI Kebayoran Baru" className="min-h-[44px] rounded-xl" />
            </Field>
            <Field label="Tanggal Pindah"><DateInput testId="input-tanggal-pindah" value={form.tanggal_pindah || ""} onChange={(v) => set("tanggal_pindah", v)} /></Field>
          </div>}
          <Field label="NIK">
            <Input data-testid="input-nik" value={form.nik} onChange={(e) => set("nik", e.target.value)} className="min-h-[44px] rounded-xl font-mono" />
          </Field>
          <Field label="Nomor KK">
            <Input data-testid="input-nomor-kk" value={form.nomor_kk} onChange={(e) => set("nomor_kk", e.target.value)} disabled={linkedKeluarga} className="min-h-[44px] rounded-xl font-mono" />
            {linkedKeluarga && <p className="text-xs text-slate-500 mt-1" data-testid="nomor-kk-hint">Diturunkan otomatis dari data Keluarga yang terhubung.</p>}
          </Field>
        </Section>

        <Section value="s2" title="2. Foto Profil Jemaat">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <div className="h-32 w-32 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="foto-preview">
              {fotoPath ? (
                <img src={fileUrl(fotoPath)} alt="foto" className="h-full w-full object-cover" />
              ) : (
                <div className="text-center text-slate-400">
                  <ImageIcon className="h-8 w-8 mx-auto mb-1" />
                  <span className="text-xs">Belum Ada Foto</span>
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2 w-full">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} data-testid="foto-input" />
              {!isEdit && <p className="text-xs text-amber-600">Simpan data jemaat dulu, lalu unggah foto pada mode Edit.</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" className="rounded-xl min-h-[44px]" disabled={!isEdit || uploading} onClick={() => fileRef.current?.click()} data-testid="foto-upload-btn">
                  {fotoPath ? <RefreshCw className="h-4 w-4 mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  {fotoPath ? "Ganti Foto" : "Unggah Foto"}
                </Button>
                <Button type="button" variant="outline" className="rounded-xl min-h-[44px]" disabled={!isEdit || uploading} onClick={() => setKamera(true)} data-testid="foto-kamera-btn"><Camera className="h-4 w-4 mr-2" />Ambil Foto dari Kamera</Button>
                <KameraFotoDialog open={kamera} onClose={() => setKamera(false)} onAmbil={(f) => pickFile(f)} />
                {fotoPath && (
                  <Button type="button" variant="outline" className="rounded-xl min-h-[44px] text-red-600 border-red-200 hover:bg-red-50" disabled={uploading} onClick={handleDeleteFoto} data-testid="foto-delete-btn">
                    <Trash2 className="h-4 w-4 mr-2" /> Hapus Foto
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-400">Format JPG/PNG/WEBP, maksimal 5MB. Foto bersifat opsional. Anda dapat mengatur posisi & zoom sebelum menyimpan.</p>
            </div>
          </div>
          <PhotoCropDialog file={cropFile} open={Boolean(cropFile)} onCancel={() => setCropFile(null)} onSave={handleUpload} saving={uploading} />
        </Section>

        <Section value="s3" title="3. Kelahiran & Pernikahan">
          <Field label="Tempat Lahir"><Input data-testid="input-tempat-lahir" value={form.tempat_lahir} onChange={(e) => set("tempat_lahir", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="Tanggal Lahir"><DateInput testId="input-tanggal-lahir" value={form.tanggal_lahir} onChange={(v) => set("tanggal_lahir", v)} /></Field>
          <Field label="Status Pernikahan"><SearchableSelect options={master.status_pernikahan} value={form.status_pernikahan} onChange={changeStatusPernikahan} placeholder="Pilih status pernikahan" testId="select-status-pernikahan" /></Field>
          {form.status_pernikahan === "Menikah" && (
            <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid="pernikahan-section">
              <Field label="Tanggal Pernikahan"><DateInput testId="input-tanggal-pernikahan" value={form.tanggal_pernikahan} onChange={(v) => set("tanggal_pernikahan", v)} /></Field>
              <Field label="Tempat Pernikahan"><Input data-testid="input-tempat-pernikahan" value={form.tempat_pernikahan} onChange={(e) => set("tempat_pernikahan", e.target.value)} placeholder="Contoh: Gereja XYZ / KUA Kecamatan ..." className="min-h-[44px] rounded-xl" /></Field>
              <div className="sm:col-span-2"><Field label="Nama Suami/Istri">
                <PasanganPicker nama={form.nama_pasangan} pasanganId={form.pasangan_id} linked={pasangan} selfId={id} testId="pasangan-picker"
                  onChange={({ id: pid, nama, brief }) => { setForm((f) => ({ ...f, pasangan_id: pid || "", nama_pasangan: nama })); setPasangan(brief); }} />
              </Field></div>
            </div>
          )}
          <Dialog open={pendingStatusPernikahan !== null} onOpenChange={(o) => !o && setPendingStatusPernikahan(null)}>
            <DialogContent className="rounded-2xl" data-testid="pernikahan-confirm-dialog">
              <DialogHeader>
                <DialogTitle>Kosongkan data pernikahan?</DialogTitle>
                <DialogDescription>Mengubah status menjadi "{pendingStatusPernikahan}" akan mengosongkan Tanggal Pernikahan dan Tempat Pernikahan yang sudah tersimpan. Lanjutkan?</DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-row gap-2">
                <Button type="button" variant="outline" className="rounded-xl min-h-[44px] flex-1" onClick={() => setPendingStatusPernikahan(null)} data-testid="pernikahan-confirm-cancel">Batal</Button>
                <Button type="button" className="rounded-xl min-h-[44px] flex-1 bg-red-600 hover:bg-red-700" onClick={() => { set("status_pernikahan", pendingStatusPernikahan); setPendingStatusPernikahan(null); }} data-testid="pernikahan-confirm-ok">Ya, Kosongkan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Section>

        <Section value="s4" title="4. Alamat & Wilayah">
          <AlamatTambahan judul="Alamat sesuai KTP" required error={errors.alamat_ktp} value={form.alamat_ktp} onChange={(v) => { set("alamat_ktp", v); if (errors.alamat_ktp) setErrors((x) => ({ ...x, alamat_ktp: undefined })); }} testId="alamat-ktp" />
          <AlamatTambahan judul="Alamat Domisili" value={form.alamat_domisili} onChange={(v) => set("alamat_domisili", v)} testId="alamat-domisili" />
        </Section>

        <Section value="s5" title="5. Kontak & Komunikasi">
          <Field label="Nomor HP"><Input data-testid="input-nomor-hp" value={form.nomor_hp} onChange={(e) => set("nomor_hp", e.target.value)} className="min-h-[44px] rounded-xl font-mono" /></Field>
          <Field label="WhatsApp"><Input data-testid="input-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="min-h-[44px] rounded-xl font-mono" /></Field>
          <Field label="Email"><Input data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="Link Google Maps">
            <div className="flex gap-2">
              <Input data-testid="input-link-google-maps" value={form.link_google_maps} onChange={(e) => set("link_google_maps", e.target.value)} placeholder="https://maps.app.goo.gl/..." className="min-h-[44px] rounded-xl flex-1" />
              {form.link_google_maps && /^https?:\/\//i.test(form.link_google_maps.trim()) && <Button asChild type="button" variant="outline" className="rounded-xl min-h-[44px] shrink-0"><a href={form.link_google_maps.trim()} target="_blank" rel="noreferrer" data-testid="form-buka-maps-btn"><MapPin className="h-4 w-4 mr-1" /> Buka di Google Maps</a></Button>}
            </div>
          </Field>
        </Section>

        <Section value="s6" title="6. Data Gerejawi & Komisi">
          <Field label="Status Baptisan">
            <SearchableSelect options={master.status_baptisan} value={form.status_baptisan} onChange={(v) => set("status_baptisan", v)} placeholder="Pilih (opsional)" testId="select-status-baptisan" />
          </Field>
          {form.status_baptisan === "Sudah" && (
            <>
              <Field label="Tanggal Baptisan" required error={errors.tanggal_baptisan}>
                <DateInput testId="input-tanggal-baptisan" value={form.tanggal_baptisan} onChange={(v) => set("tanggal_baptisan", v)} />
              </Field>
              <Field label="Tempat/Gereja Baptisan">
                <Input data-testid="input-tempat-baptisan" value={form.tempat_baptisan} onChange={(e) => set("tempat_baptisan", e.target.value)} className="min-h-[44px] rounded-xl" />
              </Field>
            </>
          )}
          <Field label="Komisi"><SearchableSelect options={master.komisi} value={form.komisi} onChange={(v) => set("komisi", v)} placeholder="Pilih komisi" testId="select-komisi" /></Field>
          <Field label="Jabatan Gerejawi (opsional)"><SearchableSelect options={["Pendeta", "Evangelis", "Diaken"]} value={form.jabatan_gerejawi || ""} onChange={(v) => set("jabatan_gerejawi", v)} placeholder="Tidak ada jabatan gerejawi" testId="select-jabatan-gerejawi" />
            {form.jabatan_gerejawi && <p className="text-xs text-slate-500 mt-1" data-testid="jabatan-gerejawi-hint">Nama akan ditampilkan sebagai <b>{gelar(form.jabatan_gerejawi)} {form.nama_lengkap || "…"}</b></p>}</Field>
          {form.komisi === "Komisi Anak" && (
            <Field label="Kelas SM (diajar oleh Guru SM)"><SearchableSelect options={["Kecil", "Besar", "Menengah", "Pra Remaja"]} value={form.kelas_sm || ""} onChange={(v) => set("kelas_sm", v)} placeholder="Pilih kelas SM" testId="select-kelas-sm" /></Field>
          )}
          <Field label="Care Leader"><Input data-testid="input-care-leader" value={form.care_leader} onChange={(e) => set("care_leader", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="Bidang Pelayanan">
            <MultiSelect options={master.bidang_pelayanan} value={form.bidang_pelayanan} onChange={changeBidangPelayanan} placeholder="Pilih satu atau lebih bidang pelayanan" testId="select-bidang-pelayanan" />
          </Field>
          {["Keyboard","Guitar","Bass","Drum"].some((i) => form.bidang_pelayanan.includes(i)) && (
            <Field label="Instrumen Lain yang Dikuasai (opsional)">
              <TagInput value={form.jenis_musik} onChange={(v) => set("jenis_musik", v)} placeholder="Ketik instrumen lalu Enter, mis. Saxophone" testId="input-jenis-musik" />
            </Field>
          )}
          {form.bidang_pelayanan.includes("Lainnya") && (
            <Field label="Bidang Pelayanan Lainnya"><Input data-testid="input-pelayanan-lainnya" value={form.pelayanan_lainnya} onChange={(e) => set("pelayanan_lainnya", e.target.value)} placeholder="Contoh: Operator Kamera" className="min-h-[44px] rounded-xl" /></Field>
          )}
          <Field label="Keterangan"><Textarea data-testid="input-keterangan" value={form.keterangan} onChange={(e) => set("keterangan", e.target.value)} className="rounded-xl" rows={2} /></Field>
        </Section>

        <Section value="s7" title="7. Pendidikan & Pekerjaan">
          <Field label="Pendidikan Terakhir"><SearchableSelect options={master.pendidikan} value={form.pendidikan} onChange={(v) => set("pendidikan", v)} placeholder="Pilih pendidikan" testId="select-pendidikan" /></Field>
          <Field label="Pekerjaan"><Input data-testid="input-pekerjaan" value={form.pekerjaan} onChange={(e) => set("pekerjaan", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="Instansi/Perusahaan"><Input data-testid="input-instansi" value={form.instansi} onChange={(e) => set("instansi", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
          <Field label="Jabatan"><Input data-testid="input-jabatan" value={form.jabatan} onChange={(e) => set("jabatan", e.target.value)} className="min-h-[44px] rounded-xl" /></Field>
        </Section>

        <Section value="s8" title="8. Data Orang Tua">
          <Field label="Ayah">
            <ParentPicker label="Ayah" gender="Laki-laki" linked={parents.ayah} nama={form.nama_ayah} parentId={form.ayah_id} selfId={id}
              onChange={({ id: pid, nama, brief }) => { setForm((f) => ({ ...f, ayah_id: pid || "", nama_ayah: nama })); setParents((p) => ({ ...p, ayah: brief })); }} testId="parent-ayah" />
          </Field>
          <Field label="Ibu">
            <ParentPicker label="Ibu" gender="Perempuan" linked={parents.ibu} nama={form.nama_ibu} parentId={form.ibu_id} selfId={id}
              onChange={({ id: pid, nama, brief }) => { setForm((f) => ({ ...f, ibu_id: pid || "", nama_ibu: nama })); setParents((p) => ({ ...p, ibu: brief })); }} testId="parent-ibu" />
          </Field>
        </Section>
        {isEdit && (
          <Section value="s9" title="9. Dokumen Jemaat">
            <p className="text-xs text-slate-500">Akta Baptisan, Akta Pernikahan, Akta Penyerahan Anak, Sertifikat, dan dokumen lain (PDF/JPG/PNG). Dokumen tersimpan langsung saat diunggah.</p>
            <DokumenJemaat jemaatId={id} denganUpload />
          </Section>
        )}
      </Accordion>

      <div className="sticky bottom-16 md:bottom-4 flex gap-3 bg-white/90 backdrop-blur p-3 rounded-2xl border border-slate-200 shadow-lg">
        <Button variant="outline" className="flex-1 rounded-xl min-h-[48px]" onClick={() => navigate(-1)} data-testid="cancel-btn">Batal</Button>
        <Button className="flex-1 rounded-xl min-h-[48px] bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={submit} disabled={saving} data-testid="save-jemaat-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </div>
  );
}

const Section = ({ value, title, children }) => (
  <AccordionItem value={value} className="border border-slate-200 rounded-2xl bg-white overflow-hidden">
    <AccordionTrigger className="px-4 py-4 hover:no-underline text-base font-semibold text-slate-800">{title}</AccordionTrigger>
    <AccordionContent className="px-4 pb-4 space-y-4">{children}</AccordionContent>
  </AccordionItem>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1" data-testid="field-error">{error}</p>}
  </div>
);
